import React, { useEffect, useState } from 'react';
import { Bell, CheckCircle2 } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { NarrativeCorrelation } from './types';

export const AlertsTab: React.FC = () => {
  const [alerts, setAlerts] = useState<NarrativeCorrelation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'new' | 'acknowledged'>('all');

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await apiClient.getMyCorrelations(filter === 'all' ? undefined : filter, 100);
      setAlerts(res?.correlations ?? []);
    } catch { setAlerts([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filter]);

  const acknowledge = async (a: NarrativeCorrelation) => {
    if (a.status === 'acknowledged' || !a.my_stream_id) return;
    try {
      await apiClient.acknowledgeStreamCorrelation(a.my_stream_id, a.id);
      setAlerts(prev => prev.map(x => x.id === a.id ? { ...x, status: 'acknowledged' } : x));
    } catch { /* best-effort */ }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Bell size={14} style={{ color: '#eab308' }} />
          <p className="text-xs font-mono uppercase tracking-widest" style={{ color: '#eab308' }}>Cross-Stream Narrative Overlaps</p>
        </div>
        <div className="flex gap-1">
          {(['all', 'new', 'acknowledged'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className="px-2.5 py-1 rounded text-[10px] font-mono uppercase"
              style={{ background: filter === f ? '#eab30815' : '#111', border: `1px solid ${filter === f ? '#eab30840' : '#222'}`, color: filter === f ? '#eab308' : '#4b5563' }}>
              {f}
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-xs text-gray-600 font-mono">Loading...</p>}

      {!loading && alerts.length === 0 && (
        <div className="flex flex-col items-center py-16">
          <Bell size={32} style={{ color: '#374151', marginBottom: 16 }} />
          <p className="text-sm text-gray-600 font-mono">No narrative overlaps detected yet.</p>
          <p className="text-xs text-gray-700 mt-1 max-w-sm text-center">
            When the same real-world story surfaces across two of your monitored keywords, an alert appears here.
          </p>
        </div>
      )}

      <div className="space-y-2">
        {alerts.map(a => (
          <div key={a.id} className="rounded-lg p-4 flex items-start justify-between gap-4"
            style={{ background: '#0f0f0f', border: `1px solid ${a.status === 'new' ? '#eab30830' : '#1a1a1a'}` }}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded font-mono" style={{ background: '#eab30820', color: '#eab308' }}>
                  {Math.round(a.overlap_score * 100)}% overlap
                </span>
                <span className="text-sm text-white">"{a.my_keyword}" ↔ "{a.other_keyword}"</span>
              </div>
              {a.shared_hashtags.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {a.shared_hashtags.map(t => (
                    <span key={t} className="text-[10px] px-1.5 py-0.5 rounded font-mono" style={{ background: '#1a1a1a', color: '#a855f7' }}>#{t}</span>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-gray-600">{new Date(a.detected_at).toLocaleString()}</p>
            </div>
            {a.status === 'new' ? (
              <button onClick={() => acknowledge(a)}
                className="flex items-center gap-1 text-[11px] px-2.5 py-1 rounded font-mono flex-shrink-0"
                style={{ background: '#00ff0010', border: '1px solid #00ff0030', color: '#00ff00' }}>
                <CheckCircle2 size={11} /> Acknowledge
              </button>
            ) : (
              <span className="text-[10px] text-gray-600 flex items-center gap-1 flex-shrink-0"><CheckCircle2 size={11} /> Acknowledged</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default AlertsTab;
